import { type JSX, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { useTranslation } from 'react-i18next';

import { company } from '~/company/Company';
import { CompanyName } from '~/company/interface';
import { useGlobal } from '~/hooks/useGlobal';
import useMediaQuery from '~/hooks/useMediaQuery';

import { BREAKPOINTS } from '../../breakpoints';
import Breadcrumbs from '../../components/Breadcrumbs/Breadcrumbs';
import type { BreadcrumbsItem } from '../../stores/CatalogStore';

import MobileHeaderLayout from '../Main/MobileHeader/MobileHeaderLayout';
import MobileHeaderTitle from '../Main/MobileHeader/MobileHeaderTitle';

import styles from './Legals.module.scss';
import LegalsCD from './LegalsCD';
import LegalsLocalee from './LegalsLocalee';

import './styles.scss';
import LegalsCerise from './LegalsCerise/LegalsCerise';
import LegalsCirclek from './LegalsCirclek/LegalsCircle';
import LegalsDubaiExpress from './LegalsDubaiExpress/LegalsDubaiExpress';
import LegalsHeimkaup from './LegalsHeimkaup/LegalsHeimkaup';
import LegalsKids from './LegalsKids/LegalsKids';
import { LegalsMCosmetic } from './LegalsMCosmetic';
import LegalsOkie from './LegalsOkie/LegalsOkie';
import LegalsVilo from './LegalsVilo';

const LEGALS_NAV = [
  {
    link: 'cookie-policy',
    title: 'Cookie policy',
  },
  {
    link: 'payment-and-delivery',
    title: 'Payment and delivery T&C',
  },
  {
    link: 'privacy-policy',
    title: 'Privacy Policy',
  },
  {
    link: 'sales-and-returns-policies-and-consumer',
    title: 'Sales and Returns Policies and Consumer - Protection Procedures',
  },
  {
    link: 'terms-of-service',
    title: 'Terms Of Service',
  },
];

const Legals = (): JSX.Element => {
  const { t } = useTranslation();
  const { isTablet } = useGlobal();
  const isMobile = useMediaQuery(`(max-width: ${BREAKPOINTS.phone}px)`);

  useEffect(() => {
    setTimeout(() => {
      const anchor = window.location.hash.slice(1);
      handleLink(anchor);
    }, 0);
  }, []);

  const handleLink = (anchor: string) => {
    if (anchor) {
      const anchorEl = document.getElementById(anchor);
      if (anchorEl) {
        if (isMobile) {
          anchorEl.scrollIntoView();
        } else {
          const offset = anchorEl.offsetTop;
          const additionalOffset = isMobile
            ? 0
            : company.match({ vilo: 80, default: 200 });

          if (!isMobile) {
            window.scroll(0, offset - additionalOffset);
          }
        }
      }
    }

    if (location.hash) {
      history.replaceState(null, document.title, ' ');
    }
  };

  const companyLegals = (() => {
    switch (company.name) {
      case CompanyName.CityDrinks:
        return <LegalsCD handleLink={handleLink} />;
      case CompanyName.Localee:
        return <LegalsLocalee handleLink={handleLink} />;
      case CompanyName.Vilo:
        return <LegalsVilo handleLink={handleLink} />;
      case CompanyName.CircleK:
        return <LegalsCirclek handleLink={handleLink} />;
      case CompanyName.Kids:
        return <LegalsKids handleLink={handleLink} />;
      case CompanyName.Okie:
        return <LegalsOkie handleLink={handleLink} />;
      case CompanyName.Heimkaup:
        return <LegalsHeimkaup handleLink={handleLink} />;
      case CompanyName.Cerise:
        return <LegalsCerise handleLink={handleLink} />;
      case CompanyName.DubaiExpress:
      case CompanyName.DubaiExpressCom:
        return <LegalsDubaiExpress handleLink={handleLink} />;
      case CompanyName.MCosmetic:
        return <LegalsMCosmetic handleLink={handleLink} />;
      default:
        return null;
    }
  })();

  if (companyLegals) {
    return (
      <>
        <Helmet>
          <meta
            data-react-helmet="true"
            name="robots"
            content="index, follow"
          />
          <title>{t('meta:legalsTitle')}</title>
          <meta name="description" content={t('meta:legalsDescription')} />
        </Helmet>
        {companyLegals}
      </>
    );
  }

  return (
    <>
      <Helmet>
        <meta data-react-helmet="true" name="robots" content="index, follow" />
        <title>{t('meta:legalsTitle')}</title>
        <meta name="description" content={t('meta:legalsDescription')} />
      </Helmet>
      <MobileHeaderLayout
        content={<MobileHeaderTitle text={t('termsAndConditions')} />}
      />
      <div className="content-layout legals">
        <div className={styles.root}>
          {!isTablet && (
            <>
              <Breadcrumbs
                items={
                  [
                    {
                      text: t('home'),
                      link: '/',
                    },
                    {
                      text: t('termsAndConditions'),
                      link: '/legals',
                    },
                  ].filter(Boolean) as BreadcrumbsItem[]
                }
              />

              <h1 className={styles.title}>{t('legals')}</h1>
            </>
          )}

          <div className={styles.page}>
            <div className={styles.page__links_container}>
              <ul className={styles.page__links}>
                {LEGALS_NAV.map((i) => (
                  <li className={styles.page__links_item} key={i.link}>
                    <a
                      href={`#${i.link}`}
                      onClick={(e) => {
                        e.preventDefault();
                        handleLink(i.link);
                      }}
                    >
                      {i.title}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
            <div className={styles.page__content}>
              <section>
                <h2 id="cookie-policy">Cookie policy</h2>
                <h3>Will be added soon</h3>
              </section>

              <section>
                <h2 id="payment-and-delivery">
                  Payment and delivery Terms and Conditions
                </h2>
                <h3>Will be added soon</h3>
              </section>

              <section>
                <h2 id="privacy-policy">Privacy Policy</h2>
                <h3>Will be added soon</h3>
              </section>

              <section>
                <h2 id="sales-and-returns-policies-and-consumer">
                  Sales and Returns Policies and Consumer - Protection
                  Procedures
                </h2>
                <h3>Will be added soon</h3>
              </section>

              <section>
                <h2 id="terms-of-service">Terms Of Service</h2>
                <h3>Will be added soon</h3>
              </section>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Legals;
